import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Owner from '@ember/owner';
import type Modals from './modals';
import type { ComponentLike } from '@glint/template';
import { getCurrentPosition } from '../utils/geolocation';
import { isNativePlatform } from '../utils/native-platform';

/** Coordinate representation matching geolocation.ts */
export interface Coordinates {
  latitude: number;
  longitude: number;
}

/** Permission states for location access */
export type PermissionState =
  'unknown' | 'prompt' | 'granted' | 'denied' | 'dismissed';

/** Location status for the settings toggle */
export type LocationStatus = 'unsupported' | 'blocked' | 'on' | 'off';

/** localStorage key for dismissal timestamp */
const DISMISSAL_KEY = 'favoris_location_dismissed';
/** Cooldown period for showing the modal again after dismissal (24 hours) */
const DISMISSAL_COOLDOWN = 24 * 60 * 60 * 1000;
/** localStorage key for user location consent */
const LOCATION_CONSENT_KEY = 'favoris_location_consent';

/**
 * Location Service
 *
 * Manages user location state and permission flow. Handles the custom
 * permission modal on native platforms before triggering the OS-level
 * permission prompt.
 */
export default class LocationService extends Service {
  @tracked permissionState: PermissionState = 'unknown';
  @tracked userLocation: Coordinates | null = null;
  @tracked isLoading = false;

  private permissionChecked = false;

  constructor(owner: Owner) {
    super(owner);
    this.checkExistingPermission();
  }

  /**
   * Check if we should show the permission modal to the user
   */
  get shouldPrompt(): boolean {
    // If permission already determined, don't prompt
    if (this.permissionChecked) {
      return false;
    }

    // If user previously dismissed, check cooldown
    const dismissalTimestamp = this.getDismissalTimestamp();
    if (dismissalTimestamp) {
      const timeSinceDismissal = Date.now() - dismissalTimestamp;
      if (timeSinceDismissal < DISMISSAL_COOLDOWN) {
        return false;
      }
    }

    return true;
  }

  /**
   * Show the permission modal, then request location if accepted
   *
   * @param modals - The modals service to display the custom modal
   * @returns The user's coordinates if location was granted, null otherwise
   */
  async requestWithPrompt(modals: Modals): Promise<Coordinates | null> {
    if (!this.shouldPrompt) {
      // Already have permission or user is in cooldown, fetch directly
      return this.fetchLocation();
    }

    // Import and show the custom modal using the path alias
    const module =
      await import('favoris-web/components/location-permission-modal');
    // @ts-expect-error - Component signature is compatible but type system can't verify
    const LocationPermissionModal = module.default as ComponentLike<unknown>;

    const result = await modals.openComponent<'accepted' | 'dismissed'>({
      component: LocationPermissionModal,
      dismissible: false,
      size: 'sm',
    });

    if (result === 'accepted') {
      return this.fetchLocation();
    }

    // User dismissed - record timestamp
    this.recordDismissal();
    this.permissionState = 'dismissed';
    return null;
  }

  /**
   * Direct location fetch (triggers native permission prompt if not already granted)
   *
   * @returns The user's coordinates if successful, null otherwise
   */
  async fetchLocation(): Promise<Coordinates | null> {
    this.isLoading = true;

    try {
      const position = await getCurrentPosition();
      this.isLoading = false;

      if (position) {
        this.userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        this.permissionState = 'granted';
        this.permissionChecked = true;
        this.setConsent();
        return this.userLocation;
      }

      this.permissionState = 'denied';
      this.permissionChecked = true;
      return null;
    } catch {
      this.isLoading = false;
      this.permissionState = 'denied';
      this.permissionChecked = true;
      return null;
    }
  }

  /**
   * Clear the dismissal timestamp (for testing or manual reset)
   */
  clearDismissal(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(DISMISSAL_KEY);
    }
  }

  /**
   * Current location permission status for the settings toggle, without
   * triggering a permission prompt.
   * - 'unsupported': no geolocation API available (web only)
   * - 'blocked': permission was explicitly denied — can only be undone in
   *   browser/device settings, not re-prompted from JS
   * - 'on': permission granted and user has opted in
   * - 'off': user has opted out (cleared consent), or permission not yet granted
   */
  async getLocationStatus(): Promise<LocationStatus> {
    const isNative = await isNativePlatform();

    const consented = this.hasConsent();

    if (isNative) {
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        const perm = await Geolocation.checkPermissions();
        if (perm.location === 'denied' && perm.coarseLocation === 'denied') {
          return 'blocked';
        }
        const granted =
          perm.location === 'granted' || perm.coarseLocation === 'granted';
        return granted && consented ? 'on' : granted ? 'off' : 'off';
      } catch {
        return 'unsupported';
      }
    }

    if (!('geolocation' in navigator)) return 'unsupported';

    if ('permissions' in navigator) {
      try {
        const permStatus = await navigator.permissions.query({
          name: 'geolocation',
        });
        if (permStatus.state === 'denied') return 'blocked';
        return permStatus.state === 'granted' && consented ? 'on' : 'off';
      } catch {
        // Permissions API present but 'geolocation' unsupported (older Safari) — fall through.
      }
    }

    return 'off';
  }

  /**
   * Open the device's location/app settings screen (Android only). Used
   * when permission has been denied — the browser/OS won't let JS
   * re-prompt, so the user has to flip it on manually.
   */
  async openNativeSettings(): Promise<void> {
    try {
      const { NativeSettings, AndroidSettings } =
        await import('capacitor-native-settings');
      await NativeSettings.openAndroid({
        option: AndroidSettings.Location,
      });
    } catch (err) {
      console.error('[location] openNativeSettings failed:', err);
    }
  }

  /**
   * Clear the cached location and reset in-memory state. Does not (and
   * cannot, from JS) revoke OS/browser-granted permission — only stops
   * this app from using a previously-fetched location.
   */
  clearLocation(): void {
    this.userLocation = null;
    this.permissionState = 'unknown';
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('favoris_geolocation_cache');
      localStorage.removeItem(LOCATION_CONSENT_KEY);
    }
  }

  private checkExistingPermission(): void {
    // On native platforms, we could check the permission state here
    // For now, we'll determine it when the user interacts
    this.permissionState = 'unknown';
  }

  private hasConsent(): boolean {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(LOCATION_CONSENT_KEY) === 'true';
  }

  private setConsent(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LOCATION_CONSENT_KEY, 'true');
    }
  }

  private getDismissalTimestamp(): number | null {
    if (typeof localStorage === 'undefined') return null;

    const stored = localStorage.getItem(DISMISSAL_KEY);
    if (!stored) return null;

    const timestamp = parseInt(stored, 10);
    return isNaN(timestamp) ? null : timestamp;
  }

  private recordDismissal(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DISMISSAL_KEY, Date.now().toString());
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    location: LocationService;
  }
}
